import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Orders } from '../../../../../../modules/API/Orders/orders';
import useWebSocket from '../../../../../../generic/hooks/useWebSocket';
import { handleNotifications } from '../../../../../../generic/utils/handleNotifications';
import selectBrodMigdal from '../../../../../../generic/utils/selectBrodMigdal';
import { whoCanSeeOrder } from '../../helpers/orderPermissions';
import {
    filterOrders,
    getOrdersWarehouseFromSession,
    ORDERS_WAREHOUSE_SESSION_KEY,
} from '../../helpers/orderFilter';
import { getDefaultWarehouseID } from '../../../../../../generic/utils/warehouseDefaults';
import { sortByFieldDesc } from '../../helpers/orderSort';
import { ClientHash, OrderItem, OrderUser, SortableField } from '../../types';
import { Defaults } from '../../../../../../modules/API/Defaults/defaults';
import { IWarhouseListItem } from '../../../../../../modules/API/Defaults/models/warhouseList';
import useDataFetcher from '../../../knisot/hooks/useFetchData';
import { ORDER_STATUS, OrderStatusValue } from '../../../../../../generic/ui/Selectors/OrderStatusSelector';
import {
    ORDER_AKPACA_TYPE,
    OrderAkpacaTypeValue,
} from '../../../../../../generic/ui/Selectors/OrderAkpacaTypeSelector';

const ORDERS_WS_URL = 'wss://my.agroline.co.il/ws/ws';

const useOrdersList = () => {
    const user = useSelector((s: any) => s.mainReducer.user) as OrderUser;
    const defaults = useSelector((s: any) => s.defaults?.defaults);
    const clientHash = (useSelector(
        (s: any) => s?.defaults?.clientList?.hashTable,
    ) as ClientHash) || ({} as ClientHash);
    const isSellerSeeAllOrders = !!selectBrodMigdal(defaults, 364, 'Logical_Value');

    const [orders, setOrders] = useState<OrderItem[]>([]);
    const [input, setInput] = useState('');
    const [statusId, setStatusId] = useState<OrderStatusValue>(ORDER_STATUS.ALL);
    const [warehouseID, setWarehouseIDState] = useState<number>(() =>
        getOrdersWarehouseFromSession(getDefaultWarehouseID(user?.tranzit)),
    );
    const [typeFilter, setTypeFilter] = useState<OrderAkpacaTypeValue>(ORDER_AKPACA_TYPE.ALL);
    const [groupID, setGroupID] = useState<number>(0);
    const [sortedValue, setSortedValue] = useState<SortableField | ''>('');
    const [isLoading, setIsLoading] = useState(false);

    const { socket, message } = useWebSocket(ORDERS_WS_URL);
    const { data: warehouseList } = useDataFetcher<IWarhouseListItem[]>(Defaults.getWarhouseList, 'data');

    // Derived on top of the full list, so socket updates go through the same filters.
    const azmanot = useMemo(() => {
        const filtered = filterOrders(orders, { input, statusId, warehouseID, typeFilter, groupID });
        return sortedValue ? sortByFieldDesc(filtered, sortedValue) : filtered;
    }, [orders, input, statusId, warehouseID, typeFilter, groupID, sortedValue]);

    const setWarehouseID = useCallback((id: number) => {
        sessionStorage.setItem(ORDERS_WAREHOUSE_SESSION_KEY, id.toString());
        setWarehouseIDState(id);
    }, []);

    const loadOrders = useCallback(async (dateStart: string, dateEnd: string) => {
        setIsLoading(true);
        try {
            const res = await Orders.getOrdersShort(dateStart, dateEnd);
            const sorted = res
                .sort((a: OrderItem, b: OrderItem) => +a.Delivery_Date - +b.Delivery_Date)
                .reverse();
            setOrders(sorted);
        } catch (err: any) {
            const message = err?.response?.data?.message;
            const isJwt =
                message === 'jwt malformed' ||
                message === 'jwt expired' ||
                message === 'קונטקסט לא פעיל, כנס מחדש למערכת';
            if (isJwt) {
                handleNotifications('התנתקת מהמערכת, אנא כנס מחדש', { type: 'error' });
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const removeOrder = useCallback(
        async (OrderID: number) => {
            try {
                const res = await Orders.deleteOrder(OrderID);
                if (res?.message) {
                    handleNotifications(res.message, { type: 'success' });
                }
                let removed: OrderItem | undefined;
                setOrders(prev => {
                    removed = prev.find(e => e.OrderID === OrderID);
                    return prev.filter(o => o.OrderID !== OrderID);
                });
                if (removed) {
                    try {
                        await Orders.passNewOrderToWS({ ...removed, Status: -1 });
                    } catch {
                        // swallow — same as legacy
                    }
                }
                Orders.logOrderStatusHistory(OrderID, user, -1);
            } catch (err: any) {
                const message = err?.response?.data?.message;
                if (message) {
                    handleNotifications(message, { type: 'error' });
                }
            }
        },
        [user],
    );

    useEffect(() => {
        let incoming: OrderItem | null = null;
        try {
            incoming = JSON.parse(message);
        } catch {
            incoming = null;
        }
        if (!incoming) return;
        const next = incoming;
        setOrders(prev => {
            const existing = prev.find(e => e.OrderID === next.OrderID);
            if (existing) {
                if (next.Status === -1) {
                    return prev.filter(e => e.OrderID !== next.OrderID);
                }
                // Merge: socket payloads may omit fields (Type, WarehouseID) the filters rely on.
                return prev.map(e => (e.OrderID === next.OrderID ? { ...e, ...next } : e));
            }
            if (next.Status === -1) return prev;
            if (whoCanSeeOrder(user, next.CustomerID, clientHash, isSellerSeeAllOrders)) {
                return [next, ...prev];
            }
            return prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message, socket]);

    return {
        azmanot,
        input,
        statusId,
        warehouseID,
        typeFilter,
        groupID,
        warehouseList,
        sortedValue,
        isLoading,
        user,
        loadOrders,
        setInput,
        setStatusId,
        setWarehouseID,
        setTypeFilter,
        setGroupID,
        setSortedValue,
        removeOrder,
    };
};

export default useOrdersList;
